/* eslint complexity: ["error", 7] */
import { Input, Flex } from '@cof/plastic-components';
import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { styled, css } from 'styled-components';
import { InputWrapper } from './SharedStyledComponents';
import ErrorHandler, { shouldDisplayRadioTick } from './ErrorHandler';
import { LabelSection } from '../../../components/TextField/TextField';
import { FORM_SECTIONS } from './FormIDs';
import Group from '../../../components/Group';
import { Input as DateInput } from '../../../components/DateField/DatePicker/DatePickerInput';
import Label from '../../../components/Label/Label';
import { autoCompleteMapping } from '../../../utilities/constants';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';
import { getAriaDescribedBy } from '../../../utilities/utils';
import { withText } from '../../../text/textStore';
import PropTypes from 'prop-types';
import { useFormData } from '../../../components/FormDataContext';

const SmallInput = css`
    ${({ theme }: any) => css`
        padding-top: ${theme.space.xs};

        display: inline-block;
        flex: 1 2 0;
        min-width: 8.5rem;
    `};
`;

const Day = styled.div`
    ${SmallInput}
`;

const Month = styled.div`
    ${SmallInput}
`;

const Year = styled.div`
    ${SmallInput}
    flex: 2 1 0;
    min-width: auto;
`;

const DateOfBirthInput = ({ Text }: any) => {
    const {
        register,
        formState: { errors },
        watch,
        setValue,
        trigger
    } = useFormContext();
    const aboutYouIDs = FORM_SECTIONS.AboutYou;

    const [dateFields, setDateFields] = useState({
        day: '',
        month: '',
        year: ''
    });
    const dayInputRef = useRef(null);

    const [dateTouched, setDateTouched] = useState({
        day: false,
        month: false,
        year: false
    });

    const { isEdit } = useFormData();

    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();

    const dateOfBirthValue = watch(aboutYouIDs.DateOfBirth);
    /* TODO: Further Improvement - register the fields to use react-hook-form state management
    and delete the individual fields from the form data prior to submission, the same as InterestedInBT
    */
    const allTouched = dateTouched.day && dateTouched.month && dateTouched.year;
    const hasError = errors[aboutYouIDs.DateOfBirth];
    const shouldRevealError = allTouched || submitAttempted || isEdit;

    const dateFieldVariant = () => {
        if (hasError && shouldRevealError) return 'error';
        if ((allTouched || isEdit) && dateOfBirthValue && !hasError) return 'success';
        return 'default';
    };
    const shouldShowDateError = () => hasError && shouldRevealError;

    useEffect(() => {
        if (dateOfBirthValue && !Object.values(dateFields).filter((value) => value !== '').length) {
            const [year, month, day] = dateOfBirthValue.split('-');
            setDateFields({
                day: day || '',
                month: month || '',
                year: year || ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateOfBirthValue]);

    useEffect(() => {
        const { day, month, year } = dateFields;
        if (day && month && year) {
            const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            setValue(aboutYouIDs.DateOfBirth, formattedDate, {
                shouldValidate: true
            });
        }
    }, [dateFields, setValue, aboutYouIDs.DateOfBirth]);

    const calculateAge = ({ year, month, day }: any) => {
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age -= 1;
        }
        return age;
    };

    const leapYearValidator = (day: any, month: any, year: any) => {
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        return !(day == 0 || day > lastDayOfMonth);
    };

    const ageRangeValidator = (day: any, month: any, year: any) => {
        const age = calculateAge({ year, month, day });
        return !(age < 18 || age > 130 || year < 100);
    };

    return (
        <InputWrapper py="xs">
            <Group as={'fieldset'}>
                <LabelSection
                    id="DateOfBirthHeader"
                    label={<Text path="DateOfBirth"></Text>}
                    as="legend"
                    // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                    displayTick={shouldDisplayRadioTick(aboutYouIDs.DateOfBirth, watch, errors)}
                    data-qm-allow
                ></LabelSection>

                <Flex gap="0.5rem">
                    <Day>
                        <Label
                            fontSize={['textSmallMobile', 'textSmallDesktop']}
                            fontWeight={'500'}
                            mt="0"
                            color={'global.grey500'}
                            htmlFor={aboutYouIDs.DoB}
                            data-qm-allow
                        >
                            Day
                        </Label>
                        <DateInput
                            id={aboutYouIDs.DoB}
                            ref={dayInputRef}
                            value={dateFields.day}
                            data-qa-id={aboutYouIDs.DoB}
                            onBlur={() => {
                                setDateTouched((prev) => ({ ...prev, day: true }));
                                trigger(aboutYouIDs.DateOfBirth);
                            }}
                            onChange={(e: any) => {
                                const day = e.target.value;
                                setDateFields((prev) => ({ ...prev, day }));
                            }}
                            maxLength={2}
                            // @ts-expect-error TS2769: No overload matches this call.
                            variant={dateFieldVariant()}
                            autoComplete={`${autoCompleteMapping['date-of-birth-day']}`}
                            aria-describedby={getAriaDescribedBy(aboutYouIDs.DateOfBirth, submitAttempted, [], errors)}
                        />
                    </Day>

                    <Month>
                        <Label
                            fontSize={['textSmallMobile', 'textSmallDesktop']}
                            fontWeight={'500'}
                            mt="0"
                            color={'global.grey500'}
                            htmlFor={aboutYouIDs.MoB}
                            data-qm-allow
                        >
                            Month
                        </Label>
                        <DateInput
                            id={aboutYouIDs.MoB}
                            value={dateFields.month}
                            data-qa-id={aboutYouIDs.MoB}
                            onBlur={() => {
                                setDateTouched((prev) => ({ ...prev, month: true }));
                                trigger(aboutYouIDs.DateOfBirth);
                            }}
                            onChange={(e: any) => {
                                const month = e.target.value;
                                setDateFields((prev) => ({ ...prev, month }));
                            }}
                            maxLength={2}
                            // @ts-expect-error TS2769: No overload matches this call.
                            variant={dateFieldVariant()}
                            autoComplete={`${autoCompleteMapping['date-of-birth-month']}`}
                            aria-describedby={getAriaDescribedBy(aboutYouIDs.DateOfBirth, submitAttempted, [], errors)}
                        />
                    </Month>

                    <Year>
                        <Label
                            fontSize={['textSmallMobile', 'textSmallDesktop']}
                            fontWeight={'500'}
                            mt="0"
                            color={'global.grey500'}
                            htmlFor={aboutYouIDs.YoB}
                            data-qm-allow
                        >
                            Year
                        </Label>
                        <DateInput
                            id={aboutYouIDs.YoB}
                            value={dateFields.year}
                            data-qa-id={aboutYouIDs.YoB}
                            onBlur={() => {
                                setDateTouched((prev) => ({ ...prev, year: true }));
                                trigger(aboutYouIDs.DateOfBirth);
                            }}
                            onChange={(e: any) => {
                                const year = e.target.value;
                                setDateFields((prev) => ({ ...prev, year }));
                            }}
                            maxLength={4}
                            // @ts-expect-error TS2769: No overload matches this call.
                            variant={dateFieldVariant()}
                            autoComplete={`${autoCompleteMapping['date-of-birth-year']}`}
                            aria-describedby={getAriaDescribedBy(aboutYouIDs.DateOfBirth, submitAttempted, [], errors)}
                        />
                    </Year>

                    <Input
                        id={aboutYouIDs.DateOfBirth}
                        data-qa-id={aboutYouIDs.DateOfBirth}
                        type="hidden"
                        {...register(aboutYouIDs.DateOfBirth, {
                            required: 'Date of birth is required',
                            pattern: {
                                value: /^[0-9]{4}-(0?[1-9]|1[0-2])-(3[01]|[12][0-9]|0?[1-9])$/,
                                message: 'This is not a valid date of birth'
                            },
                            validate: (value: any) => {
                                const [year, month, day] = value.split('-');
                                if (year && month && day) {
                                    const isValid =
                                        leapYearValidator(day, month, year) && ageRangeValidator(day, month, year);
                                    return isValid || "That doesn't look right, please check it and try again";
                                }
                                return true;
                            }
                        })}
                    />
                </Flex>
            </Group>
            <ErrorHandler
                id={aboutYouIDs.DateOfBirth}
                show={shouldShowDateError()}
                fieldRef={dayInputRef}
                data-qm-allow
            />
        </InputWrapper>
    );
};

DateOfBirthInput.propTypes = {
    Text: PropTypes.func.isRequired
};

export default withText('AboutYou')(DateOfBirthInput);
